/* eslint-disable max-lines */
import React, {
 useContext, useState, useEffect
} from 'react';
import {
 useParams, useNavigate
} from 'react-router-dom';
import {
 Context
} from 'shared/context/GlobalState';
import {
 Card, CardContent, CardHeader, CardTitle
} from 'shared/components/ui/card';
import AlertModal from 'shared/components/Alertmodal';
import Breadcrumb from 'shared/components/Breadcrumb';
import {
    channelService, opcodeAssistantService, assistantProvisioningService
} from 'shared/api';
import {
  Switch
} from 'shared/components/ui/switch';
import {
  Label
} from 'shared/components/ui/label';
import {
  BotOff
} from 'lucide-react';
import {
  NestedPageLayout
} from 'shared/components/NewDesignComponents/PageLayout';
import withLoader from 'shared/hooks/withLoader';
import {
    PanelErrorBoundary
} from 'shared/components/ErrorBoundary';
import {
  Input
} from 'shared/components/ui/input';
import {
  Textarea
} from 'shared/components/ui/textarea';
import {
  Button
} from 'shared/components/ui/button';
import {
  Plus, X, Copy, Check, Info, ExternalLink, RefreshCw, Send
} from 'lucide-react';
import OpcodeInstructionDialog from '../components/AssistantOpcode';
import {
  Assistant, Channel
} from '../types/builderAssistant.types';
import {
  AssistantStatus,
  UpdateOpcodeAssistantRequest
} from '../../../shared/api/services/bexService.types';
import {
    Tabs, TabsContent, TabsList, TabsTrigger
} from 'shared/components/ui/tabs';
import AssistantTest from './AssistantTest';

export default function AssistantDetails() {
  const {
 oragID, subOragID, assistantId
} = useParams<{ oragID: string; subOragID: string; assistantId: string }>();

  const fetchAssistant = async () => {
    const response = await opcodeAssistantService.getOpcodeAssistant(oragID, subOragID, assistantId);
    return response.data;
  };

  const MyComponentWithLoader = withLoader(ActiveAssistantComponent, fetchAssistant);

  return (
      <NestedPageLayout className={''}>
          <div className="my-3">
              <Breadcrumb maxPaths={2} />
          </div>

          {/* Tier 3 — the stored assistant document (opcodes, channels,
              guidelines) renders below; the breadcrumb above survives it. */}
          <PanelErrorBoundary
              name="assistant-details"
              title="This assistant could not be displayed"
              recordType="assistant"
              recordId={assistantId}
              resetKeys={[assistantId]}
          >
              <MyComponentWithLoader />
          </PanelErrorBoundary>
      </NestedPageLayout>
  );
}

interface ActiveAssistantComponentProps {
  data: Assistant;
}

const ActiveAssistantComponent = ({
  data
}: ActiveAssistantComponentProps) => {
   const [btnLoading, setBtnLoading] = useState(false);
   const [switchLoading, setSwitchLoading] = useState(false);
   const [showDelModal, setShowDelModal] = useState(false);
   const [deletedChannel, setDeletedChannel] = useState<Channel | null>(null);
   const [assistant, setAssistant] = useState<Assistant>(data);
   // NOTE: A-0006.03 removed `contextItems` and the streaming flags from the
   // Assistant config (they're runtime concerns, not stored on the doc). This
   // page still hosts a legacy test panel — we keep them as local state so the
   // existing UX works without persisting anything to the doc.
   const [contextItems, setContextItems] = useState<{ key: string; value: string }[]>(
     ((data as unknown as { contextItems?: { key: string; value: string }[] }).contextItems) || []
   );
   // `assistant.channels` in A-0006.03 is a policy object {mode, authorized_channels}
   // — NOT an array of channel records. The legacy list below renders attached
   // channel entities; in the old shape Server merged those into the GET response
   // as an array. Until this page is re-wired to fetch channels via channelService,
   // the legacy array view is empty when the new rich shape is returned.
   const attachedChannels: Channel[] = Array.isArray((assistant as unknown as { channels?: unknown }).channels)
     ? (assistant as unknown as { channels: Channel[] }).channels
     : [];
   const [copied, setCopied] = useState(false);
   const [linkCopied, setLinkCopied] = useState(false);
   const [keyCopied, setKeyCopied] = useState(false);
   const [rotatingKey, setRotatingKey] = useState(false);
   const [emailInput, setEmailInput] = useState('');
   const [sendingEmail, setSendingEmail] = useState(false);
   const [payloadText, setPayloadText] = useState('');
   const [provisioningInfo, setProvisioningInfo] = useState<{
     instance_id: string;
     auth_key: string;
     configuration_url: string;
     status: string;
     template_id: string;
     provisioned_at: string;
   } | null>(null);

  const {
  oragID, subOragID
} = useParams<{ oragID: string; subOragID: string }>();
  const {
  addNewNotifcation
} = useContext(Context);
  const navigate = useNavigate();

  useEffect(() => {
    setPayloadText(JSON.stringify(executionPayload(), null, 2));
  }, [contextItems, assistant]);

  useEffect(() => {
    assistantProvisioningService
      .getProvisioningInfo(assistant.assistant_id, oragID!, subOragID!)
      .then((res) => {
        if (res.data?.data) {
          setProvisioningInfo(res.data.data);
        }
      })
      .catch(() => {});
  }, [assistant.assistant_id, oragID, subOragID]);

const handleToggleActive = () => {
  setSwitchLoading(true);

const nextStatus: AssistantStatus =
  assistant.status === 'active' ? 'inactive' : 'active';

const updateObj: UpdateOpcodeAssistantRequest = {
  assistantId: assistant.assistant_id,
  org_id: oragID,
  sub_org_id: subOragID,
  status: nextStatus,
} as UpdateOpcodeAssistantRequest;

  opcodeAssistantService
    .updateOpcodeAssistant(updateObj)
    .then(() => {

      // NOTE: handleToggleActive originally also pruned a deleted channel
      // from prev.channels — but in A-0006.03 `channels` is the auth policy
      // dict, not a list of attached channels. The prune was mis-wired to the
      // wrong handler anyway (removal lives in handleRemoveAssistantChannel).
      // Toggle is now pure: no channel list mutation here.
      setAssistant((prev) => ({
 ...prev,
status: nextStatus
}));
    })
    .catch(() => {

    })
    .finally(() => {
      setSwitchLoading(false);
    });
};

  const handleRemoveAssistantChannel = async () => {
    setBtnLoading(true);
    try {
      // S-0005: update keys are snake_case; assistant_id is immutable server-side
      // (unbinding an assistant is no longer supported via channel update).
      const channelsObj = {
        org_id: oragID || '',
        sub_org_id: subOragID || '',
        channelId: deletedChannel?._id || '',
      };
      await channelService.updateChannel(channelsObj);
      setDeletedChannel(null);
      // NOTE: legacy pruned `channels` as an array of channel records. In
      // A-0006.03 `channels` is the auth policy dict — removing a channel
      // instance is a channel-service concern, not a mutation of
      // assistant.channels. The local list view will refresh on next load.
      addNewNotifcation('channel removed successfully', 'success');
      setShowDelModal(false);
    } catch (error) {
      if (error.response?.status === 409) {
        addNewNotifcation('Assistant must have at least one channel connected to it', 'danger');
      } else {
        addNewNotifcation('Something went wrong', 'danger');
      }

    } finally {
      setBtnLoading(false);
    }
  };

  const handleRemoveEmailChannel = (item: Channel) => {
    setDeletedChannel(item);
    setShowDelModal(true);
  };

  const addContextItem = () => {
    setContextItems([...contextItems, {
        key: '',
        value: ''
}]);
  };

  const updateContextItem = (index: number, key: string, value: string) => {
    const newItems = [...contextItems];
    newItems[index] = {
        key,
        value
    };
    setContextItems(newItems);
  };

  const removeContextItem = (index: number) => {
    setContextItems(contextItems.filter((_, i) => i !== index));
  };

  // A-0007.05 (2026-04-22): display-only sample — shows the exact A2A v1.0
  // JSON-RPC SendMessage envelope the AssistantTest panel will send. Context
  // key-values the user types on this page are converted to A2A Message Parts
  // with BeX typed-input metadata so curl-from-copy is authoritative.
  const executionPayload = () => {
    const firstAuthorizedChannel =
      (assistant as any)?.channels?.authorized_channels?.[0] || null;

    const parts: Array<Record<string, any>> = contextItems
      .filter((item) => item.key && item.value !== undefined)
      .map((item) => {
        let parsed: any = item.value;
        try { parsed = JSON.parse(item.value); } catch { /* keep as string */ }
        if (typeof parsed === 'string') {
          return {
            type: 'text',
            text: parsed,
            metadata: {
 category: 'context',
name: item.key
},
          };
        }
        return {
          type: 'data',
          data: parsed,
          metadata: {
 category: 'context',
name: item.key
},
        };
      });

    if (parts.length === 0) {
      parts.push({
        type: 'text',
        text: '',
        metadata: {
 category: 'context',
name: 'user_message'
},
      });
    }

    const bexExtensions: Record<string, any> = {};
    if (firstAuthorizedChannel) bexExtensions.channelId = firstAuthorizedChannel;

    return {
      _endpoint: `/projecto/execute/api/v1/a2a/${oragID}/${subOragID}/${assistant.assistant_id}/rpc`,
      // The DE's token is never returned to the browser, so this sample can only
      // ever show a placeholder — the caller supplies the token they set.
      _authorization: 'Bearer <the assistant_token you set on this DE>',
      _content_type: 'application/a2a+json',
      envelope: {
        jsonrpc: '2.0',
        id: 1,
        method: 'SendMessage', // use SendStreamingMessage for SSE
        params: {
          message: {
            role: 'user',
            parts,
            contextId: `sample-${Date.now()}`, // = memory_log_id
          },
          configuration: Object.keys(bexExtensions).length > 0
            ? {
 bexExtensions
}
            : undefined,
        },
      },
    };
  };

  const copyPayload = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(executionPayload(), null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('copy failed', err);
    }
  };

  const shareableLink = provisioningInfo
    ? `${window.location.origin}/provisioned-config/auth?instance=${provisioningInfo.instance_id}`
    : '';

  const copyShareableLink = async () => {
    try {
      await navigator.clipboard.writeText(shareableLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('copy failed', err);
    }
  };

  const copyAuthKey = async () => {
    if (!provisioningInfo) return;
    try {
      await navigator.clipboard.writeText(provisioningInfo.auth_key);
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2000);
    } catch (err) {
      console.error('copy failed', err);
    }
  };

  const handleRotateKey = async () => {
    setRotatingKey(true);
    try {
      const res = await assistantProvisioningService.rotateAuthKey(
        assistant.assistant_id, oragID!, subOragID!
      );
      if (res.data?.data?.auth_key) {
        setProvisioningInfo(prev => prev ? {
 ...prev,
auth_key: res.data.data.auth_key
} : prev);
        addNewNotifcation('Auth key rotated successfully. All previous links are now invalid.', 'success');
      }
    } catch {
      addNewNotifcation('Failed to rotate auth key', 'danger');
    } finally {
      setRotatingKey(false);
    }
  };

  const handleSendConfigEmail = async () => {
    if (!emailInput.trim()) {
      addNewNotifcation('Please enter an email address', 'warning');
      return;
    }
    setSendingEmail(true);
    try {
      console.log(assistant);
      await assistantProvisioningService.sendConfigEmail(
        assistant.assistant_id, oragID!, subOragID!, emailInput.trim(), assistant.title
      );
      addNewNotifcation(`Configuration link sent to ${emailInput.trim()}`, 'success');
      setEmailInput('');
    } catch {
      addNewNotifcation('Failed to send configuration email', 'danger');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
      <Tabs defaultValue="details" className="w-full">
          <TabsList className="mb-4">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="test">Testing</TabsTrigger>
          </TabsList>

          <TabsContent value="details">
              <div className="flex flex-col md:flex-row w-full gap-3">
                  <Card className="flex-grow">
                      <CardHeader>
                          <CardTitle>{assistant.title}</CardTitle>
                      </CardHeader>
                      <CardContent className="flex flex-col gap-4">
                          <div>
                              <Label className="mb-2 block">Context (free-shape JSON dictionary)</Label>
                              <div>
                                  <Label className="text-sm text-muted-foreground">Key-Value Pairs</Label>
                                  <div className="space-y-2">
                                      {contextItems.map((item, index) => (
                                          <div key={index} className="flex items-center gap-2">
                                              <Input
                                              placeholder="Key"
                                              value={item.key}
                                              onChange={(e) => updateContextItem(index, e.target.value, item.value)}
                                              className="flex-1"
                                          />
                                              <Input
                                              placeholder="Value (can be JSON string)"
                                              value={item.value}
                                              onChange={(e) => updateContextItem(index, item.key, e.target.value)}
                                              className="flex-1"
                                          />
                                              <Button
                                              variant="outline"
                                              size="sm"
                                              onClick={() => removeContextItem(index)}
                                          >
                                                  <X className="h-4 w-4" />
                                              </Button>
                                          </div>
                                  ))}
                                      <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={addContextItem}
                                      className="w-full"
                                  >
                                          <Plus className="h-4 w-4 mr-2" />
                                          Add Context Key
                                      </Button>
                                  </div>
                              </div>
                          </div>
                          <div>
                              <Label className="mb-2 block">A2A Execution Payload (JSON-RPC)</Label>
                              <div className="relative w-full">
                                  <Textarea
                                  value={payloadText || JSON.stringify(executionPayload(), null, 2)}
                                  onChange={(e) => setPayloadText(e.target.value)}
                                  className="min-h-[250px] text-xs font-mono"
                              />
                                  <Button
                                  variant="outline"
                                  size="sm"
                                  className="absolute top-2 right-2"
                                  onClick={copyPayload}
                              >
                                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                  </Button>
                              </div>
                          </div>
                      </CardContent>
                  </Card>

                  <Card className="w-full md:w-[350px] ml-auto">
                      <CardHeader>
                          <div className="flex w-full justify-between">
                              <CardTitle>AI Assistant Status</CardTitle>
                              <Switch disabled={switchLoading} checked={assistant.status === 'active'} onCheckedChange={handleToggleActive} />
                          </div>
                      </CardHeader>
                      <CardContent className="flex flex-col items-center justify-center gap-4">
                          <div className="space-y-2 flex flex-col items-center">
                              {assistant.status === 'active' ? (
                                  <BotIcon className="w-[100px] h-[100px] text-primary" />
              ) : (
                  <BotOff className="w-[100px] h-[100px] text-primary" />
              )}
                              <p className="text-lg font-semibold">AI Assistant is {assistant.status === 'active' ? 'Active' : 'Inactive'}</p>
                              <p className="text-muted-foreground">
                                  {assistant.status === 'active' && 'The AI assistant is ready to assist you.'}
                              </p>
                          </div>
                      </CardContent>
                  </Card>
              </div>

              {/* channels card */}
              <Card className="mt-3">
                  <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle>Channels</CardTitle>
                  </CardHeader>
                  <CardContent className="flex justify-between w-full overflow-hidden">
                      <div className="flex flex-col gap-3 w-full">
                          {attachedChannels.length > 0 ? (
              attachedChannels.map((channel) => (
                  <div
                      key={channel._id}
                      className="flex justify-between items-center p-2 rounded hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => navigate(`../../channels/${channel._id}`)}
                  >
                      <div className="flex items-center gap-2 flex-grow max-w-[70%]">
                          <MailOpenIcon className="min-w-6 min-h-6 text-muted-foreground" />
                          <div className="w-full">
                              <p className="truncate font-medium">{channel.topic}</p>
                              <p className="text-muted-foreground text-xs">{channel?.created?.split('T')[0] || 'N/A'}</p>
                          </div>
                      </div>
                      <div className="flex flex-col gap-3" onClick={(e) => e.stopPropagation()}>
                          <Button onClick={() => handleRemoveEmailChannel(channel)} variant="destructive" size="sm">
                              Unsubscribe
                          </Button>
                      </div>
                  </div>
              ))
            ) : (
                <h3>No channels found for this assistant</h3>
            )}
                      </div>
                  </CardContent>
              </Card>

              {/* provisioning info card */}
              {provisioningInfo && (
                  <Card className="mt-3">
                      <CardHeader className="flex flex-row items-center gap-2">
                          <Info className="h-5 w-5 text-muted-foreground" />
                          <CardTitle>Provisioning Info</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-6">
                          {/* Shareable Configuration Link */}
                          <div>
                              <Label className="text-sm font-medium mb-2 block">Configuration Link</Label>
                              <p className="text-xs text-muted-foreground mb-2">
                                  Share this link along with the auth key. The user will be prompted to enter the key.
                              </p>
                              <div className="flex items-center gap-2">
                                  <Input
                                      readOnly
                                      value={shareableLink}
                                      className="font-mono text-xs flex-1"
                                  />
                                  <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={copyShareableLink}
                                      className="shrink-0"
                                  >
                                      {linkCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                      <span className="ml-1">{linkCopied ? 'Copied' : 'Copy'}</span>
                                  </Button>
                                  <Button
                                      size="sm"
                                      onClick={() => {
                                          document.cookie = `provisioned-config-key=${provisioningInfo.auth_key}; path=/; SameSite=Strict`;
                                          window.open(`/provisioned-config?instance=${provisioningInfo.instance_id}`, '_blank');
                                      }}
                                      className="shrink-0 gap-1"
                                  >
                                      <ExternalLink className="h-4 w-4" />
                                      Open
                                  </Button>
                              </div>
                          </div>

                          {/* Auth Key with Rotate */}
                          <div>
                              <Label className="text-sm font-medium mb-2 block">Auth Key</Label>
                              <div className="flex items-center gap-2">
                                  <Input
                                      readOnly
                                      value={provisioningInfo.auth_key}
                                      className="font-mono text-xs flex-1"
                                  />
                                  <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={copyAuthKey}
                                      className="shrink-0"
                                  >
                                      {keyCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                  </Button>
                                  <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={handleRotateKey}
                                      disabled={rotatingKey}
                                      className="shrink-0 gap-1"
                                  >
                                      <RefreshCw className={`h-4 w-4 ${rotatingKey ? 'animate-spin' : ''}`} />
                                      Rotate
                                  </Button>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                  Rotating the key will invalidate all previously shared links.
                              </p>
                          </div>

                          {/* Send via Email */}
                          <div>
                              <Label className="text-sm font-medium mb-2 block">Send Configuration Link</Label>
                              <div className="flex items-center gap-2">
                                  <Input
                                      type="email"
                                      placeholder="email@example.com"
                                      value={emailInput}
                                      onChange={(e) => setEmailInput(e.target.value)}
                                      className="flex-1"
                                  />
                                  <Button
                                      size="sm"
                                      onClick={handleSendConfigEmail}
                                      disabled={sendingEmail}
                                      className="shrink-0 gap-1"
                                  >
                                      <Send className="h-4 w-4" />
                                      {sendingEmail ? 'Sending...' : 'Send'}
                                  </Button>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                  Send the configuration link to an email address.
                              </p>
                          </div>

                          {/* Metadata Grid */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                  <Label className="text-sm text-muted-foreground">Instance ID</Label>
                                  <p className="text-sm font-mono break-all">{provisioningInfo.instance_id}</p>
                              </div>
                              <div>
                                  <Label className="text-sm text-muted-foreground">Status</Label>
                                  <p className="text-sm">{provisioningInfo.status}</p>
                              </div>
                              <div>
                                  <Label className="text-sm text-muted-foreground">Template ID</Label>
                                  <p className="text-sm font-mono break-all">{provisioningInfo.template_id}</p>
                              </div>
                              <div>
                                  <Label className="text-sm text-muted-foreground">Provisioned At</Label>
                                  <p className="text-sm">{provisioningInfo.provisioned_at ? new Date(provisioningInfo.provisioned_at).toLocaleString() : 'N/A'}</p>
                              </div>
                          </div>
                      </CardContent>
                  </Card>
              )}

              {/* opcode card */}
              <OpcodeInstructionDialog />

              {/* alert modal */}
              <AlertModal
        openModal={showDelModal}
        setopenModal={setShowDelModal}
        onDelete={handleRemoveAssistantChannel}
        loading={btnLoading}
        title="Are you sure you want to remove this channel"
        btnTitle="Confirm"
      />
          </TabsContent>
          <TabsContent value="test" className='h-full'>
              <AssistantTest assistant={assistant} />
          </TabsContent>
      </Tabs>
  );
};

function BotIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
      <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
          <path d="M12 8V4H8" />
          <rect width="16" height="12" x="4" y="8" rx="2" />
          <path d="M2 14h2" />
          <path d="M20 14h2" />
          <path d="M15 13v2" />
          <path d="M9 13v2" />
      </svg>
  );
}

function MailOpenIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
      <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
          <path d="M21.2 8.4c.5.38.8.97.8 1.6v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V10a2 2 0 0 1 .8-1.6l8-6a2 2 0 0 1 2.4 0l8 6Z" />
          <path d="m22 10-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 10" />
      </svg>
  );
}